import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, BookOpen, Users, Phone, Mail, MessageCircle } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/contexts/CurrencyContext";
import CurrencySelector from "@/components/CurrencySelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setFeaturedProducts(response.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 95% OFF Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white py-2 px-4">
        <p className="text-center text-sm sm:text-base font-bold animate-pulse">
          🔥 MASSIVE SALE: 95% OFF ALL FORMULAS! Limited Time Only! 🔥
        </p>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <nav className="hidden md:flex gap-8 items-center">
              <Link to="/products" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Products
              </Link>
              <Link to="/bundle" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Bundle Deal
              </Link>
              <Link to="/subscriptions" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Subscriptions
              </Link>
              <Link to="/suppliers" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Suppliers
              </Link>
              <a href="#about" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                About
              </a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Contact
              </a>
              <CurrencySelector />
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
        <div 
          className="absolute inset-0 opacity-20" 
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1737703121444-c568a9d3bc0e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="hero-title">
              Unlock the Secrets to <br />
              <span className="text-sky-400">Manufacturing Success</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-12 max-w-2xl mx-auto" style={{ lineHeight: '1.6' }}>
              Download proven formulas and business packages to start your manufacturing business today. Over 500+ formulations with supplier contacts and expert support.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/products">
                <Button 
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  data-testid="browse-formulas-btn"
                >
                  Browse Formulas
                </Button>
              </Link>
              <a href="#about">
                <Button 
                  variant="outline" 
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm transition-all"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Choose Our Formula Library?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to start and scale your manufacturing business
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid="feature-formulas">
              <div className="bg-sky-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-7 w-7 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                500+ Proven Formulas
              </h3>
              <p className="text-slate-600" style={{ lineHeight: '1.6' }}>
                Access detailed manufacturing formulas for detergents, cosmetics, cleaning products, and more.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid="feature-support">
              <div className="bg-sky-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                24/7 Expert Support
              </h3>
              <p className="text-slate-600" style={{ lineHeight: '1.6' }}>
                Get help anytime you need it. Our team is available around the clock to answer your questions.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid="feature-suppliers">
              <div className="bg-sky-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <ShoppingBag className="h-7 w-7 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Supplier Connections
              </h3>
              <p className="text-slate-600" style={{ lineHeight: '1.6' }}>
                Direct access to raw material suppliers and equipment vendors to get started quickly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Deal Banner */}
      <section className="py-16 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <span className="text-sm font-medium">Best Value Deal</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Build Your Own Bundle
              </h2>
              <p className="text-lg text-emerald-100 max-w-xl">
                Choose any <strong>10 formulas</strong> from our entire library for just <strong>$5</strong>. 
                Mix and match cosmetics, cleaning products, detergents - you decide!
              </p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="text-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                <p className="text-5xl font-bold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$5</p>
                <p className="text-emerald-100">for 10 formulas</p>
              </div>
              <Link to="/bundle">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-testid="bundle-cta-btn">
                  Build My Bundle
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Featured Business Packages
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Start your manufacturing business today with our comprehensive packages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-products-grid">
            {featuredProducts.map((product) => (
              <Link 
                key={product.id} 
                to={`/products/${product.id}`}
                className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:border-slate-300"
                data-testid={`product-card-${product.id}`}
              >
                {/* Discount Badge */}
                {product.discount_percentage > 0 && (
                  <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {product.discount_percentage}% OFF
                  </div>
                )}
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={product.image_url} 
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-sky-600 font-medium mb-2">{product.category}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {product.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      {product.original_price && product.original_price !== product.price && (
                        <span className="text-sm text-slate-400 line-through" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {formatPrice(product.original_price)}
                        </span>
                      )}
                      <span className="text-2xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {formatPrice(product.price)}
                      </span>
                    </div>
                    <Button 
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                      size="sm"
                      data-testid={`view-product-btn-${product.id}`}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button 
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                data-testid="view-all-products-btn"
              >
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Entrepreneurs"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Empowering Entrepreneurs Worldwide
              </h2>
              <p className="text-lg text-slate-600 mb-6" style={{ lineHeight: '1.6' }}>
                At Formula Library, we believe in empowering people through knowledge and skills. Our comprehensive business packages provide everything you need to start your manufacturing business successfully.
              </p>
              <p className="text-lg text-slate-600 mb-8" style={{ lineHeight: '1.6' }}>
                With over 500+ proven formulas, detailed mixing guidelines, supplier directories, and 24/7 expert support, you'll have all the tools needed to build a thriving business.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="bg-sky-100 rounded-full p-1 mt-1">
                    <div className="bg-sky-500 rounded-full w-2 h-2" />
                  </div>
                  <span className="text-slate-700">Comprehensive formulation guides with step-by-step instructions</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-sky-100 rounded-full p-1 mt-1">
                    <div className="bg-sky-500 rounded-full w-2 h-2" />
                  </div>
                  <span className="text-slate-700">Direct supplier contacts and raw material sourcing</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-sky-100 rounded-full p-1 mt-1">
                    <div className="bg-sky-500 rounded-full w-2 h-2" />
                  </div>
                  <span className="text-slate-700">Market research and business strategy guidance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get in Touch
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Have questions? Our support team is here to help you 24/7
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a 
              href="https://wa.me/27730758123" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all text-center"
              data-testid="whatsapp-contact"
            >
              <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                WhatsApp
              </h3>
              <p className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                +27 730 758 123
              </p>
            </a>

            <a 
              href="tel:+27730758123"
              className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all text-center"
              data-testid="phone-contact"
            >
              <div className="bg-sky-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-7 w-7 text-sky-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Phone
              </h3>
              <p className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                +27 730 758 123
              </p>
            </a>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all text-center">
              <div className="bg-sky-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-sky-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Support
              </h3>
              <p className="text-slate-600">
                24/7 Available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Success Stories from Our Community
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join thousands of entrepreneurs who have built successful businesses using our formulas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="h-5 w-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "Started my detergent business with their formulas. Now supplying to 50+ retailers in my city. The formulas are spot-on!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-200 rounded-full flex items-center justify-center">
                  <span className="text-sky-700 font-bold">TM</span>
                </div>
                <div>
                  <p className="font-bold text-slate-900">Thabo Mokoena</p>
                  <p className="text-sm text-slate-600">South Africa</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="h-5 w-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "The cosmetics formulas are professional grade. My skincare line is now in 3 retail stores. Worth every penny!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-200 rounded-full flex items-center justify-center">
                  <span className="text-sky-700 font-bold">SA</span>
                </div>
                <div>
                  <p className="font-bold text-slate-900">Sarah Adeyemi</p>
                  <p className="text-sm text-slate-600">Nigeria</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="h-5 w-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "The subscription is amazing value. I've tested 15 different product lines and found my niche. Highly recommended!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-200 rounded-full flex items-center justify-center">
                  <span className="text-sky-700 font-bold">JP</span>
                </div>
                <div>
                  <p className="font-bold text-slate-900">John Patel</p>
                  <p className="text-sm text-slate-600">India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription CTA */}
      <section className="py-24 bg-gradient-to-br from-sky-500 to-sky-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get Unlimited Access to All Formulas
            </h2>
            <p className="text-lg sm:text-xl text-sky-100 mb-8">
              Subscribe and save thousands with unlimited access to 1,200+ business formulas. New formulas added monthly!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                <p className="text-4xl font-bold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$4.99</p>
                <p className="text-sky-100">per month</p>
              </div>
              <div className="text-2xl font-bold">OR</div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 relative">
                <div className="absolute -top-3 right-4 bg-yellow-400 text-slate-900 px-3 py-1 rounded-full text-xs font-bold">
                  BEST VALUE
                </div>
                <p className="text-4xl font-bold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$39.99</p>
                <p className="text-sky-100">per year</p>
              </div>
            </div>
            <Link to="/subscriptions">
              <Button className="bg-white text-sky-600 hover:bg-sky-50 rounded-full px-8 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-testid="subscribe-cta-btn">
                Start Your Subscription
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ShoppingBag className="h-8 w-8" />
                <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
              </div>
              <p className="text-slate-400 mb-4">
                Empowering entrepreneurs through knowledge and skills
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/products" className="text-slate-400 hover:text-white transition-colors">Browse Formulas</Link></li>
                <li><Link to="/bundle" className="text-slate-400 hover:text-white transition-colors">Bundle Deal ($5)</Link></li>
                <li><Link to="/subscriptions" className="text-slate-400 hover:text-white transition-colors">Subscriptions</Link></li>
                <li><Link to="/suppliers" className="text-slate-400 hover:text-white transition-colors">Suppliers</Link></li>
                <li><a href="#about" className="text-slate-400 hover:text-white transition-colors">About Us</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Support</h3>
              <ul className="space-y-2">
                <li>
                  <a href="tel:+27730758123" className="text-slate-400 hover:text-white transition-colors">
                    +27 730 758 123
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/27730758123" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                    WhatsApp Support
                  </a>
                </li>
                <li className="text-slate-400">24/7 Available</li>
              </ul>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Follow Us</h3>
              <div className="flex gap-4">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                  aria-label="Facebook"
                  data-testid="facebook-link"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                  aria-label="Instagram"
                  data-testid="instagram-link"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                  aria-label="YouTube"
                  data-testid="youtube-link"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-all"
                  aria-label="Twitter"
                  data-testid="twitter-link"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              </div>
              <p className="text-slate-400 text-sm mt-4">
                Connect with us on social media for updates, tips, and exclusive offers!
              </p>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm mb-4">
              © 2026 Formula Library. All rights reserved.
            </p>
            <div className="flex justify-center gap-6">
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors text-sm">Terms & Conditions</Link>
              <Link to="/refund-policy" className="text-slate-400 hover:text-white transition-colors text-sm">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
